import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import CardComponent from './CardComponent';
import SurrenderConfirmModal from './SurrenderConfirmModal';
import ProposeDrawConfirmModal from './ProposeDrawConfirmModal';
import DeclineBattleConfirmModal from './DeclineBattleConfirmModal';
export default class CardSlider extends Component {
  submitting = false;
  elementalsEl = null;
  bonusEl = null;

  // No visible controls: on desktop the card row can be grabbed and dragged
  // with the mouse; touch devices keep native finger-scroll untouched (drag
  // only activates for pointerType === 'mouse').
  rowDrag = null;
  onRowScroll = () => m.redraw();
  onResize = () => m.redraw();
  oncreate(vnode) {
    super.oncreate(vnode);
    window.addEventListener('resize', this.onResize);
  }
  onremove(vnode) {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pointermove', this.onRowPointerMove);
    window.removeEventListener('pointerup', this.onRowPointerUp);
    window.removeEventListener('pointercancel', this.onRowPointerUp);
    super.onremove(vnode);
  }
  rowEl(key) {
    return key === 'elementals' ? this.elementalsEl : this.bonusEl;
  }

  /** Start of mouse drag-scroll. Touch/pen are left alone since their native
   * swipe scrolling works better than any custom implementation. */
  onRowPointerDown = (e, key) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const el = this.rowEl(key);
    if (!el) return;
    this.rowDrag = {
      key,
      pointerId: e.pointerId,
      startX: e.clientX,
      startScrollLeft: el.scrollLeft,
      moved: false
    };
    window.addEventListener('pointermove', this.onRowPointerMove);
    window.addEventListener('pointerup', this.onRowPointerUp);
    window.addEventListener('pointercancel', this.onRowPointerUp);
  };
  onRowPointerMove = e => {
    const drag = this.rowDrag;
    if (!drag || e.pointerId !== drag.pointerId) return;
    const el = this.rowEl(drag.key);
    if (!el) return;
    const deltaX = e.clientX - drag.startX;
    if (!drag.moved && Math.abs(deltaX) > 4) {
      drag.moved = true;
      el.classList.add('is-dragging');
    }
    if (drag.moved) {
      el.scrollLeft = drag.startScrollLeft - deltaX;
    }
  };
  onRowPointerUp = () => {
    const drag = this.rowDrag;
    if (!drag) return;
    const el = this.rowEl(drag.key);
    el?.classList.remove('is-dragging');
    // If the drag actually moved the row, suppress the synthetic "click" the
    // browser fires after pointerup — otherwise the drag would accidentally
    // select whichever card the cursor landed on.
    if (drag.moved && el) el.dataset.suppressClick = '1';
    this.rowDrag = null;
    window.removeEventListener('pointermove', this.onRowPointerMove);
    window.removeEventListener('pointerup', this.onRowPointerUp);
    window.removeEventListener('pointercancel', this.onRowPointerUp);
  };

  /** Capture-phase click interceptor on the row — suppresses the "ghost"
   * click on a card right after a mouse drag (see onRowPointerUp). */
  onRowClickCapture = e => {
    const el = e.currentTarget;
    if (el.dataset.suppressClick === '1') {
      delete el.dataset.suppressClick;
      e.stopPropagation();
      e.preventDefault();
    }
  };
  view() {
    const {
      cards,
      playedCardIds,
      mana,
      battle,
      selectedElementalId,
      selectedBonusId,
      onSelectElemental,
      onSelectBonus,
      onSurrendered,
      onDrawProposed,
      onDeclined,
      onSubmitTurn,
      canSubmit
    } = this.attrs;

    // Preparation grants the actor a mana-free turn (see ProcessCardPlay.php's
    // $manaFree bypass, which this mirrors) — without this, cards the player
    // can actually afford to play for free stay wrongly greyed out here.
    const manaFree = battle.actorManaFree();

    // MODIFIER_TOURNAMENT disallows all three "back out" actions (see
    // DeclineBattleController/SurrenderController/ProposeDrawController's
    // matching isTournament() checks) — no easy outs, matching the 24h
    // clock running from turn 1.
    const isTournament = battle.modifier() === 'tournament';
    const allowDraw = !isTournament && !!app.forum.attribute('arenaAllowDraw');
    // Only reachable during the actor's own first move: neither side has
    // committed a card yet on their behalf (see DeclineBattleController's
    // matching backend check).
    const canDecline = !isTournament && battle.currentTurn() === 1;
    const available = cards.filter(c => !playedCardIds.includes(parseInt(c.id())));
    const elementals = available.filter(c => c.isElemental?.() ?? c.cardType?.() !== 'bonus');
    const bonusCards = available.filter(c => c.isBonus?.() ?? c.cardType?.() === 'bonus');
    const elementalSelected = selectedElementalId !== null;
    const bonusSelected = selectedBonusId !== null;

    // This is always the player's own hand, so the card back comes from
    // their own preference — read via arenaCardBackDetails (server-resolved)
    // rather than the client-side ARENA_CARD_BACKS lookup, which doesn't
    // carry private backs.
    const cardBackImage = app.session.user?.attribute('arenaCardBackDetails')?.imageUrl ?? null;
    return m("div", {
      className: "ArenaCardSlider"
    }, m("div", {
      className: "ArenaCardSlider-header"
    }, m("div", {
      className: "ArenaCardSlider-headerRight"
    }, canDecline && m(Button, {
      className: "Button ArenaCardSlider-decline",
      icon: "fas fa-door-closed",
      onclick: () => app.modal.show(DeclineBattleConfirmModal, {
        battle,
        onDeclined
      })
    }, app.translator.trans('forumaker-arena.forum.decline_battle.btn')), !isTournament && m(Button, {
      className: "Button ArenaCardSlider-surrender",
      icon: "fas fa-flag",
      onclick: () => app.modal.show(SurrenderConfirmModal, {
        battle,
        onSurrendered
      })
    }, app.translator.trans('forumaker-arena.forum.surrender.btn')), allowDraw && m(Button, {
      className: "Button ArenaCardSlider-proposeDraw",
      icon: "fas fa-handshake",
      onclick: () => app.modal.show(ProposeDrawConfirmModal, {
        battle,
        onProposed: onDrawProposed
      })
    }, app.translator.trans('forumaker-arena.forum.propose_draw.btn')), onSubmitTurn &&
    // Plain button (like Surrender/Propose Draw) until the turn is
    // ready, then switches to the green Button--primary style.
    m(Button, {
      className: `Button ${canSubmit ? 'Button--primary' : ''} ArenaCardSlider-submitBtn`,
      icon: "fas fa-paper-plane",
      disabled: !canSubmit || this.submitting,
      loading: this.submitting,
      onclick: () => {
        if (!canSubmit || this.submitting) return;
        this.submitting = true;
        m.redraw();
        onSubmitTurn().finally(() => {
          this.submitting = false;
          m.redraw();
        });
      }
    }, app.translator.trans('forumaker-arena.forum.card_slider.submit_turn')))), m("div", {
      className: "ArenaCardSlider-body"
    }, m("div", {
      className: "ArenaCardSlider-section"
    }, m("div", {
      className: "ArenaCardSlider-sectionLabel"
    }, m("i", {
      className: "fas fa-clone"
    }), m("span", null, app.translator.trans('forumaker-arena.forum.card_slider.main_card')), elementalSelected && m("span", {
      className: "ArenaCardSlider-badge ArenaCardSlider-badge--ok"
    }, m("i", {
      className: "fas fa-check"
    }))), m("div", {
      className: "ArenaCardSlider-cards",
      oncreate: vnode => {
        this.elementalsEl = vnode.dom;
        this.elementalsEl.addEventListener('scroll', this.onRowScroll, {
          passive: true
        });
        this.elementalsEl.addEventListener('click', this.onRowClickCapture, true);
      },
      onpointerdown: e => this.onRowPointerDown(e, 'elementals')
    }, elementals.length === 0 ? m("div", {
      className: "ArenaCardSlider-empty"
    }, app.translator.trans('forumaker-arena.forum.card_slider.no_cards')) : elementals.map(card => {
      const isSelected = selectedElementalId === parseInt(card.id());
      const isDisabled = !manaFree && card.cost() > mana || elementalSelected && !isSelected;
      return m(CardComponent, {
        key: card.id(),
        card: card,
        compact: true,
        selected: isSelected,
        disabled: isDisabled,
        cardBackImage: cardBackImage,
        onclick: !isDisabled ? onSelectElemental : undefined
      });
    }))), bonusCards.length > 0 && m("div", {
      className: "ArenaCardSlider-section ArenaCardSlider-section--bonus"
    }, m("div", {
      className: "ArenaCardSlider-sectionLabel"
    }, m("i", {
      className: "fas fa-star"
    }), m("span", null, app.translator.trans('forumaker-arena.forum.card_slider.bonus_card')), bonusSelected && m("span", {
      className: "ArenaCardSlider-badge ArenaCardSlider-badge--ok"
    }, m("i", {
      className: "fas fa-check"
    }))), m("div", {
      className: "ArenaCardSlider-cards",
      oncreate: vnode => {
        this.bonusEl = vnode.dom;
        this.bonusEl.addEventListener('scroll', this.onRowScroll, {
          passive: true
        });
        this.bonusEl.addEventListener('click', this.onRowClickCapture, true);
      },
      onpointerdown: e => this.onRowPointerDown(e, 'bonus')
    }, bonusCards.map(card => {
      const isSelected = selectedBonusId === parseInt(card.id());
      const elementalCost = elementalSelected ? elementals.find(c => parseInt(c.id()) === selectedElementalId)?.cost() ?? 0 : 0;
      const isDisabled = !manaFree && card.cost() + elementalCost > mana || bonusSelected && !isSelected;
      return m(CardComponent, {
        key: card.id(),
        card: card,
        compact: true,
        selected: isSelected,
        disabled: isDisabled,
        cardBackImage: cardBackImage,
        onclick: !isDisabled ? onSelectBonus : undefined
      });
    })))), (elementalSelected || bonusSelected) && !canSubmit && m("div", {
      className: "ArenaCardSlider-footer ArenaCardSlider-footer--warn"
    }, m("i", {
      className: "fas fa-exclamation-triangle"
    }), m("span", null, elementalSelected ? app.translator.trans('forumaker-arena.forum.card_slider.need_bonus') : app.translator.trans('forumaker-arena.forum.card_slider.need_elemental'))));
  }
}
flarum.reg.add('forumaker-arena', 'forum/components/CardSlider', CardSlider);